import { createContext, useCallback, useContext, useReducer } from 'react'
import { DEFAULT_QUESTION_COUNTS, EXAM_STAGES } from '../constants/config'
import { EXAM_GUARDS } from '../constants/examGuards'

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function shuffleOptions(q) {
  if (q.type !== 'mcq' || !q.options) return q
  const entries  = shuffle(Object.entries(q.options))
  const newKeys  = ['A', 'B', 'C', 'D', 'E'].slice(0, entries.length)
  const options  = Object.fromEntries(entries.map(([, v], i) => [newKeys[i], v]))
  const oldKey   = Object.keys(q.options).indexOf(q.correct_answer)
  const correct_answer = newKeys[entries.findIndex(([k]) => k === q.correct_answer)] ?? q.correct_answer
  return { ...q, options, correct_answer }
}

function applyShuffles(questions) {
  if (!EXAM_GUARDS.shuffle) return questions
  return shuffle(questions).map(shuffleOptions)
}

const INITIAL_STATE = {
  stage:          EXAM_STAGES.SETUP,
  mode:           'practice',
  difficulty:     'Beginner',
  numQuestions:   20,
  questionCounts: { ...DEFAULT_QUESTION_COUNTS },
  selectedExam:   null,
  attemptId:      null,
  questions:      [],
  answers:             {},
  feedbackShown:       {},
  shortAnswerResults:  {},
  currentIdx:     0,
  startTime:      null,
  durationSec:    null,
  autoSubmitted:  false,
  submitReason:   null,
  banksBuilt:     new Set(),
  results:        [],
  score:          null,
  breakdown:      {},
  weakTopics:     [],
  prepTips:       null,
  readiness:      null,
  creditsBlockedForGrading: false,
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STAGE':
      return { ...state, stage: action.payload }
    case 'SET_DIFFICULTY':
      return { ...state, difficulty: action.payload }
    case 'SET_MODE':
      return { ...state, mode: action.payload }
    case 'SET_NUM_QUESTIONS':
      return { ...state, numQuestions: action.payload }
    case 'SET_QUESTION_COUNTS':
      return { ...state, questionCounts: { ...state.questionCounts, ...action.payload } }
    // Clamps each count down to what's actually available, using the CURRENT
    // state at dispatch time — not a value captured in an async callback's
    // closure, which can be stale by the time an in-flight request resolves.
    case 'CLAMP_QUESTION_COUNTS': {
      const next = { ...state.questionCounts }
      for (const key of Object.keys(action.payload)) {
        if ((next[key] ?? 0) > (action.payload[key] ?? 0)) next[key] = action.payload[key]
      }
      return { ...state, questionCounts: next }
    }
    case 'SET_ANSWER':
      return { ...state, answers: { ...state.answers, [action.payload.idx]: action.payload.answer } }
    case 'SHOW_FEEDBACK':
      return { ...state, feedbackShown: { ...state.feedbackShown, [action.payload]: true } }
    case 'SET_SHORT_ANSWER_RESULT':
      return { ...state, shortAnswerResults: { ...state.shortAnswerResults, [action.payload.idx]: action.payload.result } }
    case 'SET_CURRENT_IDX':
      return { ...state, currentIdx: action.payload }
    case 'SET_AUTO_SUBMITTED':
      return { ...state, autoSubmitted: true, submitReason: action.payload ?? 'timer' }
    case 'MARK_BANK_BUILT': {
      const next = new Set(state.banksBuilt)
      next.add(action.payload)
      return { ...state, banksBuilt: next }
    }
    case 'SET_EXAM':
      return { ...state, selectedExam: action.payload }
    case 'SET_READINESS':
      return { ...state, readiness: action.payload }
    case 'START_EXAM':
      return {
        ...state,
        questions:     applyShuffles(action.payload.questions),
        mode:          action.payload.mode,
        attemptId:     action.payload.attemptId ?? null,
        answers:            {},
        feedbackShown:      {},
        shortAnswerResults: {},
        currentIdx:    0,
        autoSubmitted: false,
        submitReason:  null,
        startTime:     action.payload.mode === 'virtual' ? Date.now() : null,
        durationSec:   action.payload.durationSec,
        results:       [],
        score:         null,
        breakdown:     {},
        weakTopics:    [],
        prepTips:      null,
        creditsBlockedForGrading: false,
        stage:         action.payload.mode === 'virtual'
                         ? EXAM_STAGES.VIRTUAL
                         : EXAM_STAGES.PRACTICE,
      }
    case 'SET_RESULTS':
      return {
        ...state,
        results:    action.payload.results,
        score:      action.payload.score,
        creditsBlockedForGrading: !!action.payload.creditsBlockedForGrading,
        breakdown:  action.payload.breakdown,
        weakTopics: action.payload.weakTopics,
        prepTips:   action.payload.prepTips,
        stage:      EXAM_STAGES.RESULTS,
      }
    case 'RESET':
      return {
        ...INITIAL_STATE,
        banksBuilt: state.banksBuilt,
        readiness:  state.readiness,
      }
    default:
      return state
  }
}

const ExamContext = createContext(null)

export function ExamProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const reset = useCallback(() => dispatch({ type: 'RESET' }), [])
  return (
    <ExamContext.Provider value={{ state, dispatch, reset }}>
      {children}
    </ExamContext.Provider>
  )
}

export function useExam() {
  const ctx = useContext(ExamContext)
  if (!ctx) throw new Error('useExam must be used inside ExamProvider')
  return ctx
}
